import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../types';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeProcess,
  PrimeProcessUpdate,
} from '../../types/primeModel';
import { PrimeArtifactStateTransitionUpdate } from '../../types/primeArtifactStateTransition';
import { api } from '../../services/api';
import { API_ENDPOINTS, buildApiUrl } from '../../services/api/config';
import {
  fetchPrimeArtifactStates,
  fetchPrimeArtifactTransitions,
  fetchPrimeProcesses,
  createPrimeArtifactState,
  updatePrimeArtifactTransition,
  deletePrimeArtifactTransition,
  createPrimeProcess,
  updatePrimeProcess,
  deletePrimeProcess,
  fetchPrimeArtifactsHierarchy,
} from '../actions/primeArtifactActions';
import {
  selectPrimeArtifacts,
  selectFlatArtifacts,
  selectPrimeArtifactStates,
  selectPrimeArtifactTransitions,
  selectPrimeProcesses,
  selectLoading,
  selectError,
  selectConsumptionState,
  selectTransitionsFromState,
  selectTransitionsToState,
  selectProcessByTransitionId,
  selectInternalTransitionsByArtifactId,
  selectProcessTransitionsByArtifactId,
} from '../selectors/primeArtifactSelectors';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { DemandType, DemandFlowType, ArtifactStateTransitionType } from '../../types/enums';

// Basic selectors
export const usePrimeArtifacts = () => useSelector(selectPrimeArtifacts);
export const useFlatArtifacts = () => useSelector(selectFlatArtifacts);
export const usePrimeArtifactStates = () => useSelector(selectPrimeArtifactStates);
export const usePrimeArtifactTransitions = () => useSelector(selectPrimeArtifactTransitions);
export const usePrimeProcesses = () => useSelector(selectPrimeProcesses);
export const useLoading = () => useSelector(selectLoading);
export const useError = () => useSelector(selectError);

// Derived selectors with memoization
export const usePrimeArtifactById = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) =>
      state.primeArtifacts.primeArtifacts.find((artifact) => artifact.id === artifactId),
    [artifactId]
  );
  return useSelector(selector);
};

export const useStatesByArtifactId = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) =>
      state.primeArtifacts.primeArtifactStates.filter(
        (state) => state.prime_artifact_ref === artifactId
      ),
    [artifactId]
  );
  return useSelector(selector);
};

export const useTransitionsByArtifactId = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) =>
      state.primeArtifacts.primeArtifactTransitions.filter(
        (transition) => transition.prime_artifact_ref === artifactId
      ),
    [artifactId]
  );
  return useSelector(selector);
};

export const useProcessesByArtifactId = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) => {
      const processes = state.primeArtifacts.primeProcesses.filter(
        (process) => process.prime_artifact_ref === artifactId
      );
      return processes;
    },
    [artifactId]
  );
  return useSelector(selector, (prev, next) => {
    // Custom equality check to prevent unnecessary rerenders
    if (prev.length !== next.length) return false;
    return prev.every((p, i) => p.id === next[i].id);
  });
};

export const useCreationState = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) =>
      state.primeArtifacts.primeArtifactStates.find(
        (state) => state.prime_artifact_ref === artifactId && state.is_initial
      ),
    [artifactId]
  );
  return useSelector(selector);
};

export const useActiveState = (artifactId: string) => {
  const selector = useCallback(
    (state: RootState) =>
      state.primeArtifacts.primeArtifactStates.find(
        (state) => state.prime_artifact_ref === artifactId && !state.is_final && !state.is_initial
      ),
    [artifactId]
  );
  return useSelector(selector);
};

export const useConsumptionState = (artifactId: string) =>
  useSelector((state: RootState) => selectConsumptionState(state, artifactId));

export const useTransitionsFromState = (stateId: string) =>
  useSelector((state: RootState) => selectTransitionsFromState(state, stateId));

export const useTransitionsToState = (stateId: string) =>
  useSelector((state: RootState) => selectTransitionsToState(state, stateId));

export const useProcessByTransitionId = (transitionId: string) =>
  useSelector((state: RootState) => selectProcessByTransitionId(state, transitionId));

export const useInternalTransitionsByArtifactId = (artifactId: string) =>
  useSelector((state: RootState) => selectInternalTransitionsByArtifactId(state, artifactId));

export const useProcessTransitionsByArtifactId = (artifactId: string) =>
  useSelector((state: RootState) => selectProcessTransitionsByArtifactId(state, artifactId));

export const usePrimeArtifact = (artifactId?: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const states = useSelector(selectPrimeArtifactStates);
  const transitions = useSelector(selectPrimeArtifactTransitions);
  const processes = useSelector(selectPrimeProcesses);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  const selectedArtifactSelector = useCallback(
    (state: RootState) =>
      artifactId ? state.primeArtifacts.primeArtifacts.find((a) => a.id === artifactId) : undefined,
    [artifactId]
  );
  const selectedArtifact = useSelector(selectedArtifactSelector);

  const fetchArtifacts = useCallback(async () => {
    console.log('[usePrimeArtifact] Starting to fetch artifacts');
    try {
      console.log('[usePrimeArtifact] Dispatching fetchPrimeArtifactsHierarchy');
      await dispatch(fetchPrimeArtifactsHierarchy()).unwrap();
      console.log('[usePrimeArtifact] Successfully fetched artifacts hierarchy');
    } catch (err) {
      console.error('[usePrimeArtifact] Error fetching artifacts:', err);
      throw err;
    }
  }, [dispatch]);

  const fetchArtifactData = useCallback(
    async (artifactId: string) => {
      console.log('[usePrimeArtifact] Starting to fetch data for artifact:', artifactId);
      try {
        console.log('[usePrimeArtifact] Fetching states');
        await dispatch(fetchPrimeArtifactStates(artifactId)).unwrap();

        console.log('[usePrimeArtifact] Fetching transitions');
        await dispatch(fetchPrimeArtifactTransitions(artifactId)).unwrap();

        console.log('[usePrimeArtifact] Fetching processes');
        await dispatch(fetchPrimeProcesses(artifactId)).unwrap();

        console.log('[usePrimeArtifact] Successfully fetched all artifact data');
      } catch (err) {
        console.error('[usePrimeArtifact] Error fetching artifact data:', err);
        throw err;
      }
    },
    [dispatch]
  );

  const fetchStates = useCallback(async () => {
    if (!artifactId) return;
    try {
      const response = await api.get<PrimeArtifactState[]>(
        API_ENDPOINTS.PRIME_ARTIFACTS.STATES(artifactId)
      );
      dispatch(
        fetchPrimeArtifactStates.fulfilled(response.data, 'fetchPrimeArtifactStates', artifactId)
      );
    } catch (error) {
      dispatch(
        fetchPrimeArtifactStates.rejected(
          error as Error,
          'fetchPrimeArtifactStates',
          artifactId,
          'Failed to fetch states'
        )
      );
    }
  }, [dispatch, artifactId]);

  const fetchTransitions = useCallback(async () => {
    if (!artifactId) return;
    try {
      await dispatch(fetchPrimeArtifactTransitions(artifactId)).unwrap();
    } catch (error) {
      console.error('Error fetching transitions:', error);
    }
  }, [dispatch, artifactId]);

  const fetchProcesses = useCallback(async () => {
    if (!artifactId) return;
    try {
      const response = await api.get<PrimeProcess[]>(
        API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(artifactId)
      );
      dispatch(fetchPrimeProcesses.fulfilled(response.data, 'fetchPrimeProcesses', artifactId));
    } catch (error) {
      dispatch(
        fetchPrimeProcesses.rejected(
          error as Error,
          'fetchPrimeProcesses',
          artifactId,
          'Failed to fetch processes'
        )
      );
    }
  }, [dispatch, artifactId]);

  const createState = useCallback(
    async (state: Omit<PrimeArtifactState, 'id'>) => {
      if (!artifactId) return;
      try {
        const response = await api.post<PrimeArtifactState>(
          API_ENDPOINTS.PRIME_ARTIFACTS.STATES(artifactId),
          state
        );
        dispatch(
          createPrimeArtifactState.fulfilled(response.data, 'createPrimeArtifactState', state)
        );
        return response.data;
      } catch (error) {
        dispatch(
          createPrimeArtifactState.rejected(
            error as Error,
            'createPrimeArtifactState',
            state,
            'Failed to create state'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  const createTransition = useCallback(
    async (transition: Partial<PrimeArtifactStateTransition>) => {
      if (!artifactId) return;
      try {
        const mappedTransition = {
          name: transition.name || '',
          description: transition.description || '',
          prime_artifact_ref: artifactId,
          prime_process_ref: transition.prime_process_ref || '',
          from_state_ref: transition.from_state_ref || '',
          to_state_ref: transition.to_state_ref || '',
          default_transition_type:
            transition.default_transition_type || ArtifactStateTransitionType.OPERATIONAL,
          is_secondary: transition.is_secondary || false,
          transition_parameters: transition.transition_parameters || {},
          validation_rules: transition.validation_rules || {},
          demand_type: transition.demand_type || DemandType.TARGET,
          demand_flow_type: transition.demand_flow_type || DemandFlowType.DRUM,
          improvement_ref: transition.improvement_ref || null,
          improvement_parameters: transition.improvement_parameters || {},
          is_improvement: transition.is_improvement ?? false,
        };
        const createdTransition = await PrimeArtifactService.createPrimeArtifactTransition(
          artifactId,
          mappedTransition
        );
        await fetchArtifacts();
        return createdTransition;
      } catch (error) {
        console.error('Error creating transition:', error);
        throw error;
      }
    },
    [fetchArtifacts, artifactId]
  );

  const updateTransition = useCallback(
    async (transitionId: string, transition: PrimeArtifactStateTransition) => {
      if (!artifactId) return;
      try {
        // Only send the fields that are allowed in the update schema
        const updateData: PrimeArtifactStateTransitionUpdate = {
          default_transition_type: transition.default_transition_type,
          transition_parameters: transition.transition_parameters,
          validation_rules: transition.validation_rules,
          demand_type: transition.demand_type,
          demand_flow_type: transition.demand_flow_type,
        };

        const response = await api.put<PrimeArtifactStateTransition>(
          API_ENDPOINTS.PRIME_ARTIFACTS.UPDATE_TRANSITION(artifactId, transitionId),
          updateData
        );
        dispatch(
          updatePrimeArtifactTransition.fulfilled(response.data, 'updatePrimeArtifactTransition', {
            artifact_id: artifactId,
            transition_id: transitionId,
            transition: updateData,
          })
        );
        return response.data;
      } catch (error) {
        dispatch(
          updatePrimeArtifactTransition.rejected(
            error as Error,
            'updatePrimeArtifactTransition',
            { artifact_id: artifactId, transition_id: transitionId, transition },
            'Failed to update transition'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  const deleteTransition = useCallback(
    async (transitionId: string) => {
      if (!artifactId) return;
      try {
        await api.delete(API_ENDPOINTS.PRIME_ARTIFACTS.DELETE_TRANSITION(artifactId, transitionId));
        dispatch(
          deletePrimeArtifactTransition.fulfilled(transitionId, 'deletePrimeArtifactTransition', {
            artifactId,
            transitionId,
          })
        );
      } catch (error) {
        dispatch(
          deletePrimeArtifactTransition.rejected(
            error as Error,
            'deletePrimeArtifactTransition',
            { artifactId, transitionId },
            'Failed to delete transition'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  const createProcess = useCallback(
    async (process: Omit<PrimeProcess, 'id'>) => {
      if (!artifactId) return;
      try {
        console.log('Creating process for artifact:', artifactId);
        console.log('Process data:', process);
        const url = buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BASE);
        console.log('Create URL:', url);
        const response = await api.post<PrimeProcess>(url, process);
        console.log('Process created successfully:', response.data);
        dispatch(createPrimeProcess.fulfilled(response.data, 'createPrimeProcess', process));
        return response.data;
      } catch (error) {
        console.error('Error creating process:', error);
        dispatch(
          createPrimeProcess.rejected(
            error as Error,
            'createPrimeProcess',
            process,
            'Failed to create process'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  const updateProcess = useCallback(
    async (processId: string, process: PrimeProcess) => {
      if (!artifactId) return;
      try {
        const url = buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(artifactId));
        const updateData: PrimeProcessUpdate = {
          name: process.name,
          description: process.description,
          process_type: process.process_type,
          demand_flow_type: process.demand_flow_type,
          process_parameters: process.process_parameters,
          validation_rules: process.validation_rules,
          improvement_ref: process.improvement_ref,
          improvement_parameters: process.improvement_parameters,
        };
        const response = await api.put<PrimeProcess>(`${url}${processId}`, updateData);
        dispatch(updatePrimeProcess.fulfilled(response.data, 'updatePrimeProcess', process));
        return response.data;
      } catch (error) {
        console.error('Error updating process:', error);
        dispatch(
          updatePrimeProcess.rejected(
            error as Error,
            'updatePrimeProcess',
            process,
            'Failed to update process'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  const deleteProcess = useCallback(
    async (processId: string) => {
      if (!artifactId) return;
      try {
        await api.delete(
          buildApiUrl(`${API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId)}/processes/${processId}`)
        );
        dispatch(deletePrimeProcess.fulfilled(processId, 'deletePrimeProcess', processId));
      } catch (error) {
        console.error('Error deleting process:', error);
        dispatch(
          deletePrimeProcess.rejected(
            error as Error,
            'deletePrimeProcess',
            processId,
            'Failed to delete process'
          )
        );
        throw error;
      }
    },
    [dispatch, artifactId]
  );

  return {
    primeArtifacts: selectedArtifact,
    states,
    transitions,
    processes,
    loading,
    error,
    fetchArtifacts,
    fetchArtifactData,
    fetchStates,
    fetchTransitions,
    fetchProcesses,
    createState,
    createTransition,
    updateTransition,
    deleteTransition,
    createProcess,
    updateProcess,
    deleteProcess,
  };
};
